import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["map", "tokenLayer", "assetLayer", "token", "grid", "dialogue", "fogCanvas"]
  static values = {
    id: Number,
    gridSize: { type: Number, default: 32 },
    cols: Number,
    rows: Number,
    canEdit:    { type: Boolean, default: false },
    fogEnabled: { type: Boolean, default: false },
    fogCells:   { type: Array,   default: [] },
    fogOpacity: { type: Number,  default: 1.0 },
  }

  connect() {
    this.selectedToken = null
    this.dragState = null
    this.contextMenu = null

    // Fog state — initialised from data attributes, updated by broadcasts
    this._fogEnabled = this.fogEnabledValue
    this._fogCells   = this.fogCellsValue.length > 0
      ? [...this.fogCellsValue]
      : new Array(this.colsValue * this.rowsValue).fill(false)

    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      { channel: "StageChannel", id: this.idValue },
      { received: (data) => this.handleBroadcast(data) }
    )

    if (this.hasTokenLayerTarget) {
      this.tokenLayerTarget.addEventListener("pointerdown", (e) => this.onPointerDown(e))
      this.tokenLayerTarget.addEventListener("pointermove", (e) => this.onPointerMove(e))
      this.tokenLayerTarget.addEventListener("pointerup",   (e) => this.onPointerUp(e))
      this.tokenLayerTarget.addEventListener("contextmenu", (e) => this.onContextMenu(e))
    }

    this._dismissMenu = () => this.dismissContextMenu()
    this._escMenu = (e) => { if (e.key === "Escape") this.dismissContextMenu() }
    document.addEventListener("click", this._dismissMenu)
    document.addEventListener("keydown", this._escMenu)

    requestAnimationFrame(() => this.drawFog())
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
    if (this.consumer) this.consumer.disconnect()
    this.dismissContextMenu()
    document.removeEventListener("click", this._dismissMenu)
    document.removeEventListener("keydown", this._escMenu)
  }

  // ── Pointer events (drag-and-drop) ──────────────────────────────────

  onPointerDown(e) {
    if (e.button !== 0) return
    const tokenEl = e.target.closest("[data-token-id]")
    if (!tokenEl) return

    e.preventDefault()
    this.dismissContextMenu()

    if (!this.canEditValue) {
      this.onTokenClick(tokenEl, e)
      return
    }

    this.tokenLayerTarget.setPointerCapture(e.pointerId)

    const pctW = 100 / this.colsValue
    const pctH = 100 / this.rowsValue

    // Drop preview cell (shown as the token is dragged)
    const preview = document.createElement("div")
    preview.className = "stage-drop-preview"
    preview.style.width = `${pctW}%`
    preview.style.height = `${pctH}%`
    preview.style.display = "none"
    this.tokenLayerTarget.appendChild(preview)

    this.dragState = {
      tokenEl,
      id: Number(tokenEl.dataset.tokenId),
      ghost: null,
      preview,
      startX: e.clientX,
      startY: e.clientY,
      isDragging: false,
      targetCol: null,
      targetRow: null,
    }
  }

  onPointerMove(e) {
    if (!this.dragState) return

    const { tokenEl, startX, startY } = this.dragState
    const dx = e.clientX - startX
    const dy = e.clientY - startY

    // Cross the drag threshold (5px) to commit to a drag
    if (!this.dragState.isDragging && Math.hypot(dx, dy) > 5) {
      this.dragState.isDragging = true
      this.clearSelection()
      tokenEl.classList.add("stage-token-dragging")

      // Create ghost (visual copy that follows the cursor)
      const ghost = tokenEl.cloneNode(true)
      ghost.classList.remove("stage-token-dragging", "stage-token-selected")
      ghost.classList.add("stage-token-ghost")
      ghost.style.transition = "none"
      this.tokenLayerTarget.appendChild(ghost)
      this.dragState.ghost = ghost
    }

    if (!this.dragState.isDragging) return

    const { col, row } = this.layerGridCoords(e.clientX, e.clientY)
    this.dragState.targetCol = col
    this.dragState.targetRow = row

    const pctW = 100 / this.colsValue
    const pctH = 100 / this.rowsValue

    // Move ghost to snapped cell
    this.dragState.ghost.style.left = `${col * pctW}%`
    this.dragState.ghost.style.top  = `${row * pctH}%`

    // Move drop preview to same cell
    this.dragState.preview.style.display = ""
    this.dragState.preview.style.left = `${col * pctW}%`
    this.dragState.preview.style.top  = `${row * pctH}%`
  }

  onPointerUp(e) {
    if (!this.dragState) return
    const { tokenEl, ghost, preview, id, isDragging, targetCol, targetRow } = this.dragState

    if (ghost) ghost.remove()
    if (preview) preview.remove()
    tokenEl.classList.remove("stage-token-dragging")

    if (isDragging && targetCol !== null) {
      this.moveToken(id, targetCol, targetRow)
    } else if (!isDragging) {
      this.onTokenClick(tokenEl, e)
    }

    this.dragState = null
  }

  layerGridCoords(clientX, clientY) {
    const layer = this.tokenLayerTarget.getBoundingClientRect()
    const x = clientX - layer.left
    const y = clientY - layer.top
    const col = Math.max(0, Math.min(this.colsValue - 1, Math.floor((x / layer.width) * this.colsValue)))
    const row = Math.max(0, Math.min(this.rowsValue - 1, Math.floor((y / layer.height) * this.rowsValue)))
    return { col, row }
  }

  // ── Right-click context menu ─────────────────────────────────────────

  onContextMenu(e) {
    const tokenEl = e.target.closest("[data-token-id]")
    if (!tokenEl) return
    e.preventDefault()

    this.dismissContextMenu()
    this.clearSelection()

    const id    = Number(tokenEl.dataset.tokenId)
    const label = tokenEl.dataset.tokenLabel || "Token"
    const curHp = tokenEl.dataset.tokenCurrentHp
    const maxHp = tokenEl.dataset.tokenMaxHp
    const hasHp = curHp !== "" && curHp !== "null" && curHp !== null && curHp !== undefined

    const menu = document.createElement("div")
    menu.className = "stage-context-menu"
    menu.style.left = `${e.clientX}px`
    menu.style.top  = `${e.clientY}px`

    const header = document.createElement("div")
    header.className = "px-3 py-1.5 text-xs font-heading text-tavern-gold border-b border-tavern-border"
    header.textContent = label
    menu.appendChild(header)

    if (hasHp) {
      const hp = document.createElement("div")
      hp.className = "px-3 py-1 text-xs text-tavern-muted border-b border-tavern-border"
      hp.textContent = `HP: ${curHp} / ${maxHp}`
      menu.appendChild(hp)
    }

    if (this.canEditValue) {
      const removeBtn = document.createElement("button")
      removeBtn.className = "stage-context-item stage-context-item-danger"
      removeBtn.textContent = "Remove token"
      removeBtn.addEventListener("click", (ev) => {
        ev.stopPropagation()
        this.removeToken(id)
        this.dismissContextMenu()
      })
      menu.appendChild(removeBtn)
    }

    document.body.appendChild(menu)
    this.contextMenu = menu

    // Flip menu if it would overflow the viewport edge
    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect()
      if (rect.right > window.innerWidth)  menu.style.left = `${e.clientX - rect.width}px`
      if (rect.bottom > window.innerHeight) menu.style.top = `${e.clientY - rect.height}px`
    })
  }

  dismissContextMenu() {
    if (this.contextMenu) {
      this.contextMenu.remove()
      this.contextMenu = null
    }
  }

  // ── Token click (selection / Tavern dialogue) ────────────────────────

  onTokenClick(tokenEl, _event) {
    const id = Number(tokenEl.dataset.tokenId)
    if (this.selectedToken && this.selectedToken.id === id) {
      this.clearSelection()
      return
    }
    this.select(tokenEl)
    this.dispatch("token-click", { detail: { id, element: tokenEl } })
  }

  select(tokenEl) {
    this.clearSelection()
    tokenEl.classList.add("stage-token-selected")
    this.selectedToken = { id: Number(tokenEl.dataset.tokenId), element: tokenEl }
  }

  clearSelection() {
    if (this.selectedToken) {
      this.selectedToken.element.classList.remove("stage-token-selected")
    }
    this.selectedToken = null
  }

  // ── State mutation ───────────────────────────────────────────────────

  async moveToken(id, col, row) {
    const res = await fetch(`/stages/${this.idValue}/stage_tokens/${id}`, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
      },
      body: JSON.stringify({ stage_token: { col, row } }),
    })
    if (!res.ok) { console.warn("stage: move failed", res.status); return }
    this.applyTokenUpdate(await res.json())
  }

  async removeToken(id) {
    const res = await fetch(`/stages/${this.idValue}/stage_tokens/${id}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
    })
    if (!res.ok) { console.warn("stage: remove failed", res.status); return }
    this.applyTokenDestroy(id)
  }

  // ── Broadcast handling ───────────────────────────────────────────────

  handleBroadcast(data) {
    if (!data || !data.type) return
    switch (data.type) {
      case "token_update":
        this.applyTokenUpdate(data.token)
        this.dispatch("token-update", { detail: data.token })
        break
      case "token_create":
        this.applyTokenCreate(data.html)
        this.dispatch("token-create", { detail: {} })
        break
      case "token_destroy":
        this.applyTokenDestroy(data.id)
        this.dispatch("token-destroy", { detail: { id: data.id } })
        break
      case "asset_create":
        this.applyAssetCreate(data.html)
        break
      case "asset_update":
        this.applyAssetUpdate(data.asset)
        break
      case "asset_destroy":
        this.applyAssetDestroy(data.id)
        break
      case "encounter_update":
        this.dispatch("encounter-update", { detail: data })
        break
      case "fog_update":
        this.applyFogUpdate(data)
        break
    }
  }

  applyFogUpdate(data) {
    if (data.fog_enabled !== undefined) this._fogEnabled = data.fog_enabled
    if (data.fog_cells   !== undefined) {
      this._fogCells = (data.fog_cells && data.fog_cells.length > 0)
        ? data.fog_cells
        : new Array(this.colsValue * this.rowsValue).fill(false)
    }
    this.drawFog()
  }

  drawFog() {
    if (!this.hasFogCanvasTarget) return
    const canvas = this.fogCanvasTarget
    const ctx    = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (!this._fogEnabled) return

    const cols    = this.colsValue
    const rows    = this.rowsValue
    const cellW   = canvas.width  / cols
    const cellH   = canvas.height / rows
    const opacity = this.fogOpacityValue

    ctx.fillStyle = `rgba(0, 0, 0, ${opacity})`
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!this._fogCells[r * cols + c]) {
          ctx.fillRect(c * cellW, r * cellH, cellW + 1, cellH + 1)
        }
      }
    }
  }

  applyAssetCreate(html) {
    if (!html || !this.hasAssetLayerTarget) return
    this.assetLayerTarget.insertAdjacentHTML("beforeend", html)
  }

  applyAssetUpdate(asset) {
    if (!asset || !this.hasAssetLayerTarget) return
    const el = this.assetLayerTarget.querySelector(`[data-asset-id="${asset.id}"]`)
    if (!el) return
    el.style.left = `${asset.x * 100}%`
    el.style.top  = `${asset.y * 100}%`
    if (asset.scale !== undefined) {
      el.style.width = `${(asset.scale / this.colsValue) * 100}%`
    }
    if (asset.rotation !== undefined || asset.flip_x !== undefined) {
      const rot  = asset.rotation ?? Number(el.dataset.rotation ?? 0)
      const flip = asset.flip_x  ?? (el.dataset.flipX === "true")
      el.style.transform = `rotate(${rot}deg) scaleX(${flip ? -1 : 1})`
    }
  }

  applyAssetDestroy(id) {
    if (!this.hasAssetLayerTarget) return
    const el = this.assetLayerTarget.querySelector(`[data-asset-id="${id}"]`)
    if (el) el.remove()
  }

  applyTokenUpdate(token) {
    if (!token) return
    const el = this.tokenLayerTarget.querySelector(`[data-token-id="${token.id}"]`)
    if (!el) return
    const pctW = 100 / this.colsValue
    const pctH = 100 / this.rowsValue
    el.style.left = `${token.col * pctW}%`
    el.style.top  = `${token.row * pctH}%`
    el.dataset.tokenCol = token.col
    el.dataset.tokenRow = token.row
    if (token.current_hp !== undefined) el.dataset.tokenCurrentHp = token.current_hp
    if (token.max_hp !== undefined) el.dataset.tokenMaxHp = token.max_hp
    // Update on-token HP bar if present (TV mode)
    const fill = el.querySelector(".tv-token-hp-fill")
    if (fill && token.max_hp > 0) {
      const pct = Math.max(0, Math.min(100, (token.current_hp / token.max_hp) * 100))
      fill.style.width = `${pct}%`
      fill.style.background = pct > 60 ? "#4a8a5a" : pct > 30 ? "#c9a96e" : "#b54545"
    }
  }

  applyTokenCreate(html) {
    if (!html) return
    this.tokenLayerTarget.insertAdjacentHTML("beforeend", html)
  }

  applyTokenDestroy(id) {
    const el = this.tokenLayerTarget.querySelector(`[data-token-id="${id}"]`)
    if (el) el.remove()
  }
}
